import React, { useEffect, useRef, useState } from 'react';
import { Alert, Card, Row, Col, Select, Table, DatePicker, Radio, Button, Tag, Typography, Space, message } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import { setRolePreset } from '../../../utils/role';

const { Title, Text } = Typography;

const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// TODO: 联调时替换为接口数据 —— 核心指标
const MOCK_KPI = {
    totalCny: 256000, totalUsd: 32000,       // 累计总收益
    currentCny: 18500, currentUsd: 2300,     // 当前收益（本月）
    lastMonthCny: 22000, lastMonthUsd: 2750, // 上月收益
};

// TODO: 联调时替换为接口数据 —— 广告收益（月粒度，近12个月）
const MOCK_TREND_MONTHLY = [
    { month: '2025-09', cny: 12000, usd: 1500 },
    { month: '2025-10', cny: 13000, usd: 1600 },
    { month: '2025-11', cny: 14000, usd: 1750 },
    { month: '2025-12', cny: 15500, usd: 1900 },
    { month: '2026-01', cny: 16000, usd: 2000 },
    { month: '2026-02', cny: 14500, usd: 1800 },
    { month: '2026-03', cny: 15000, usd: 1880 },
    { month: '2026-04', cny: 16500, usd: 2050 },
    { month: '2026-05', cny: 17500, usd: 2180 },
    { month: '2026-06', cny: 19000, usd: 2380 },
    { month: '2026-07', cny: 22000, usd: 2750 },
    { month: '2026-08', cny: 18500, usd: 2300 },
];

// TODO: 联调时替换为接口数据 —— 广告收益（日粒度，近30天）
const MOCK_TREND_DAILY = (() => {
    const arr = [];
    const base = new Date('2026-08-19');
    const cnySeed = [520, 480, 560, 610, 500, 650, 580, 700, 620, 540, 720, 660, 590, 680, 640, 750, 690, 600, 730, 800, 710, 630, 760, 830, 740, 660, 790, 860, 770, 680];
    for (let i = 29; i >= 0; i--) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        const cny = cnySeed[29 - i];
        arr.push({ date: formatDate(d), cny, usd: Math.round(cny / 8) });
    }
    return arr;
})();

// TODO: 联调时替换为接口数据 —— 收益记录（近30天）
const MOCK_RECORDS = (() => {
    const arr = [];
    const base = new Date('2026-08-19');
    const apps = [
        { name: 'test-智能家居App', id: 'test-app-001', platform: 'iOS', region: '中国大陆', currency: 'CNY' },
        { name: 'test-智慧安防App', id: 'test-app-002', platform: 'Android', region: '海外', currency: 'USD' },
        { name: 'test-宠物看护App', id: 'test-app-003', platform: 'iOS', region: '海外', currency: 'USD' },
        { name: 'test-社区养老App', id: 'test-app-004', platform: 'Android', region: '中国大陆', currency: 'CNY' },
    ];
    const adPlatforms = ['taku', 'fengchuan', 'admob'];
    for (let i = 29; i >= 0; i--) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        const dateStr = formatDate(d);
        apps.forEach((app, idx) => {
            const key = (29 - i) * apps.length + idx + 1;
            const dau = 1800 + ((29 - i) * 310 + idx * 640) % 22000;
            const requestDau = Math.round(dau * (0.55 + ((idx + (29 - i)) % 4) * 0.08));
            const adRequests = requestDau * (4 + ((29 - i) % 5));
            const adImpressions = Math.round(adRequests * (0.72 + ((idx + (29 - i)) % 3) * 0.07));
            arr.push({
                key: String(key),
                date: dateStr,
                ...app,
                adPlatform: adPlatforms[(key - 1) % adPlatforms.length],
                dau,
                requestDau,
                adRequests,
                adImpressions,
                amount: app.currency === 'CNY' ? (300 + ((29 - i) * 17 + idx * 43) % 500) : (35 + ((29 - i) * 3 + idx * 7) % 60),
            });
        });
    }
    return arr;
})();

const timeRangeOptions = [
    { value: '7d', label: '近7天' },
    { value: '30d', label: '近30天' },
    { value: 'month', label: '本月' },
    { value: '3m', label: '近3个月' },
    { value: '6m', label: '近6个月' },
    { value: '1y', label: '近一年' },
];

const quickRangeOptions = [
    { value: '7d', label: '近7天' },
    { value: '15d', label: '近15天' },
    { value: '30d', label: '近30天' },
    { value: 'month', label: '本月' },
    { value: 'lastMonth', label: '上月' },
];

const getTrendData = (range) => {
    if (range === '7d' || range === '30d' || range === 'month') {
        let daily = MOCK_TREND_DAILY;
        if (range === '7d') daily = daily.slice(-7);
        if (range === 'month') daily = daily.filter((d) => d.date.startsWith('2026-08'));
        return {
            labels: daily.map((d) => d.date.slice(5)),
            cny: daily.map((d) => d.cny),
            usd: daily.map((d) => d.usd),
        };
    }
    const months = { '3m': 3, '6m': 6, '1y': 12 }[range];
    const monthly = MOCK_TREND_MONTHLY.slice(-months);
    return {
        labels: monthly.map((m) => m.month.slice(2)),
        cny: monthly.map((m) => m.cny),
        usd: monthly.map((m) => m.usd),
    };
};

const getQuickRangeDates = (range) => {
    const today = new Date('2026-08-19');
    const start = new Date(today);
    const end = new Date(today);
    switch (range) {
        case '7d': start.setDate(today.getDate() - 6); break;
        case '15d': start.setDate(today.getDate() - 14); break;
        case '30d': start.setDate(today.getDate() - 29); break;
        case 'month': start.setDate(1); break;
        case 'lastMonth':
            start.setDate(1);
            start.setMonth(start.getMonth() - 1);
            end.setDate(0);
            break;
        default: break;
    }
    return { start: formatDate(start), end: formatDate(end) };
};

// 快捷时间 → dayjs 区间（用于联动填充 RangePicker）
const getQuickRangeDayjs = (range) => {
    const today = dayjs('2026-08-19');
    switch (range) {
        case '7d': return [today.subtract(6, 'day'), today];
        case '15d': return [today.subtract(14, 'day'), today];
        case '30d': return [today.subtract(29, 'day'), today];
        case 'month': return [today.startOf('month'), today];
        case 'lastMonth': return [today.subtract(1, 'month').startOf('month'), today.subtract(1, 'month').endOf('month')];
        default: return [today.subtract(6, 'day'), today];
    }
};

const AppAdRevenue = () => {
    // 企业视角：本企业名下 App 数据
    setRolePreset('enterprise', 'app');

    const [trendRange, setTrendRange] = useState('7d');
    const [quickRange, setQuickRange] = useState('7d');
    const [customRange, setCustomRange] = useState(null);
    const [appliedRange, setAppliedRange] = useState(() => getQuickRangeDates('7d'));

    const trendRef = useRef(null);
    const trendInstance = useRef(null);

    useEffect(() => {
        const { labels, cny, usd } = getTrendData(trendRange);

        if (!trendRef.current) return;
        if (!trendInstance.current) {
            trendInstance.current = echarts.init(trendRef.current);
            trendInstance.current.setOption({
                legend: { data: ['广告收益(¥)', '广告收益($)'], top: 10, selectedMode: true },
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
                grid: { top: 60, left: 70, right: 40, bottom: 40 },
                xAxis: { type: 'category', data: labels, axisLine: { lineStyle: { color: '#999' } } },
                yAxis: { type: 'value', name: '金额', min: 0, axisLabel: { formatter: (v) => Math.floor(v) } },
                series: [
                    {
                        name: '广告收益(¥)',
                        type: 'line',
                        data: cny,
                        smooth: true,
                        areaStyle: { opacity: 0.12 },
                        lineStyle: { color: '#5470c6', width: 2.5 },
                        itemStyle: { color: '#5470c6' },
                        symbol: 'circle',
                        symbolSize: 6,
                    },
                    {
                        name: '广告收益($)',
                        type: 'line',
                        data: usd,
                        smooth: true,
                        lineStyle: { color: '#91cc75', width: 2.5 },
                        itemStyle: { color: '#91cc75' },
                        symbol: 'circle',
                        symbolSize: 6,
                    },
                ],
            });
        } else {
            trendInstance.current.setOption({
                xAxis: { data: labels },
                series: [
                    { name: '广告收益(¥)', data: cny },
                    { name: '广告收益($)', data: usd },
                ],
            });
        }
    }, [trendRange]);

    useEffect(() => {
        const handleResize = () => trendInstance.current?.resize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 收益记录筛选
    const records = MOCK_RECORDS.filter((r) => r.date >= appliedRange.start && r.date <= appliedRange.end);

    const handleQuickChange = (v) => {
        setQuickRange(v);
        setCustomRange(null);
        setAppliedRange(getQuickRangeDates(v));
    };

    const handleQuery = () => {
        if (customRange && customRange[0]) {
            setAppliedRange({
                start: customRange[0].format('YYYY-MM-DD'),
                end: customRange[1].format('YYYY-MM-DD'),
            });
        } else {
            setAppliedRange(getQuickRangeDates(quickRange));
        }
    };

    const handleReset = () => {
        setQuickRange('7d');
        setCustomRange(null);
        setAppliedRange(getQuickRangeDates('7d'));
    };

    const handleExport = () => {
        if (records.length === 0) {
            message.warning('没有可导出的数据');
            return;
        }
        // TODO: 联调时替换为接口导出
        const header = '日期,App,AppID,平台,区域,广告平台,币种,日活,请求日活,流量请求,广告展示(次数),收益金额,平台抽佣,净收益';
        const rows = records.map((r) => [
            r.date, r.name, r.id, r.platform, r.region, r.adPlatform, r.currency,
            r.dau, r.requestDau, r.adRequests, r.adImpressions,
            r.amount, 0, r.amount,
        ].join(','));
        const csvContent = '\uFEFF' + header + '\n' + rows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `广告收益导出_${new Date().toLocaleDateString()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        message.success('导出成功');
    };

    const displayRange = customRange || (quickRange ? getQuickRangeDayjs(quickRange) : null);

    const kpiMatrix = [
        { key: '1', label: '累计', cny: MOCK_KPI.totalCny, usd: MOCK_KPI.totalUsd },
        { key: '2', label: '当月', cny: MOCK_KPI.currentCny, usd: MOCK_KPI.currentUsd },
        { key: '3', label: '上月', cny: MOCK_KPI.lastMonthCny, usd: MOCK_KPI.lastMonthUsd },
    ];

    const renderAmount = (cny, usd) => (
        <div style={{ textAlign: 'right' }}>
            <div style={{ whiteSpace: 'nowrap' }}>¥{cny.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
            <div style={{ whiteSpace: 'nowrap' }}>${usd.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</div>
        </div>
    );

    const kpiColumns = [
        { title: '', dataIndex: 'label', width: 60 },
        { title: '广告总收益', key: 'total', align: 'right', render: (_, r) => renderAmount(r.cny, r.usd) },
        { title: '平台抽佣', key: 'commission', align: 'right', render: () => renderAmount(0, 0) },
        { title: '净收益', key: 'net', align: 'right', render: (_, r) => renderAmount(r.cny, r.usd) },
    ];

    const recordColumns = [
        { title: '日期', dataIndex: 'date', width: 110 },
        {
            title: 'App', key: 'app', width: 300,
            render: (_, r) => (<div>
                <div style={{ fontWeight: 500 }}>{r.name} - {r.platform} - {r.region}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{r.id}</Text>
            </div>),
        },
        {
            title: '广告平台', dataIndex: 'adPlatform', width: 100,
            render: (v) => {
                const conf = { taku: 'orange', fengchuan: 'geekblue', admob: 'green' };
                return <Tag color={conf[v] || 'default'} style={{ border: 'none' }}>{v}</Tag>;
            },
        },
        {
            title: '币种', dataIndex: 'currency', width: 80,
            render: (v) => v === 'CNY' ? <Tag color="orange" style={{ border: 'none' }}>人民币</Tag> : <Tag color="blue" style={{ border: 'none' }}>美元</Tag>,
        },
        { title: '日活', dataIndex: 'dau', width: 90, align: 'right', render: (v) => v.toLocaleString('zh-CN') },
        { title: '请求日活', dataIndex: 'requestDau', width: 100, align: 'right', render: (v) => v.toLocaleString('zh-CN') },
        { title: '流量请求', dataIndex: 'adRequests', width: 100, align: 'right', render: (v) => v.toLocaleString('zh-CN') },
        { title: '广告展示(次数)', dataIndex: 'adImpressions', width: 120, align: 'right', render: (v) => v.toLocaleString('zh-CN') },
        {
            title: '收益金额', dataIndex: 'amount', width: 130, align: 'right',
            render: (v, r) => r.currency === 'CNY'
                ? <Text strong>¥{v.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>
                : <Text strong>${v.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>,
        },
        {
            title: '平台抽佣', key: 'commission', width: 110, align: 'right',
            render: (_, r) => r.currency === 'CNY' ? <Text type="secondary">¥0.00</Text> : <Text type="secondary">$0.00</Text>,
        },
        {
            title: '净收益', key: 'net', width: 130, align: 'right',
            render: (_, r) => r.currency === 'CNY'
                ? <Text strong>¥{r.amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>
                : <Text strong>${r.amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>,
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}><Title level={4}>App广告收益</Title></div>
            <Alert
                type="warning"
                showIcon
                message="本页汇总本企业 App 接入鹤梦云 App SDK 后产生的广告收益：覆盖本企业名下 App（含各平台、区域版本），广告总收益扣除平台抽佣后即为企业净收益。"
                style={{ marginBottom: 16, borderRadius: 8 }}
            />

            {/* ═══════════ 核心指标 / 广告收益趋势 ═══════════ */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Card title="核心指标" bordered={false} bodyStyle={{ padding: 12 }} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <Table size="small" pagination={false} columns={kpiColumns} dataSource={kpiMatrix} />
                    </Card>
                </Col>
                <Col span={16}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>广告收益趋势</span>
                            <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                            <span style={{ color: '#606266', fontSize: 13 }}>时间范围</span>
                            <Select value={trendRange} onChange={setTrendRange} options={timeRangeOptions} style={{ width: 120 }} />
                        </div>
                        <div ref={trendRef} style={{ width: '100%', height: 300 }} />
                    </Card>
                </Col>
            </Row>

            {/* ═══════════ 收益记录 ═══════════ */}
            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>收益记录</span>
                    <Space size={8}>
                        <Radio.Group
                            value={quickRange}
                            onChange={(e) => handleQuickChange(e.target.value)}
                            options={quickRangeOptions}
                            optionType="button"
                            buttonStyle="solid"
                        />
                        <DatePicker.RangePicker onChange={(v) => { setCustomRange(v); if (v) setQuickRange(null); }} value={displayRange} style={{ width: 260 }} />
                        <Button type="primary" onClick={handleQuery}>查询</Button>
                        <Button onClick={handleReset}>重置</Button>
                        <Button type="primary" icon={<ExportOutlined />} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }} onClick={handleExport}>导出</Button>
                    </Space>
                </div>
                <Table columns={recordColumns} dataSource={records} rowKey="key" scroll={{ x: 1600 }} pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }} />
            </Card>
        </div>
    );
};

export default AppAdRevenue;
